import { Box, Button, useTheme } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { ROUTES } from '../../../constants/routes';
import {
  MonthlyTimesheet,
  TimesheetLog,
  TimesheetLogType,
  TimesheetStatus,
  TimesheetStatusLabel,
  User,
  UserRole,
  WorkflowStage,
} from '../../../types';
import { getAccountantTsStatusLabel } from '../../../utils/tsMappers';
import { ProgressBar } from '../../../components/ProgressBar/ProgressBar';
import { PendingStatusTooltip } from './PendingStatusTooltip';
import { calculateDaysBetweenDates } from '../../../utils/formatters';
import { Typography } from '../../../components/Typography/Typography';
import { ReactComponent as IssuedIcon } from '../../../assets/icons/status-issued.svg';
import { ReactComponent as ClosedIcon } from '../../../assets/icons/status-closed.svg';

type Props = {
  timesheet?: MonthlyTimesheet;
  operatingPartners: User[];
};

export function AccountantStatusCell({ timesheet, operatingPartners }: Props) {
  const { colors } = useTheme();
  const navigate = useNavigate();

  const statusLabel = useMemo(() => {
    return getAccountantTsStatusLabel(timesheet?.status as TimesheetStatus);
  }, [timesheet?.status]);

  // Rejected monthly timesheets stay non-clickable. Every other cell is a button, so hierarchy comes
  // from the variant: primary (filled blue) = the accountant must act, secondary = view-only status.
  const isTSDisabled = statusLabel === TimesheetStatusLabel.REJECTED;
  const isPrimary = statusLabel === TimesheetStatusLabel.SUBMITTED;

  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const yearQueryParam = params.get('year');

  const operatingPartner = useMemo(() => {
    if (operatingPartners) {
      return operatingPartners.find((op) =>
        op.monthlyTimesheets?.some((monthlyTs) => monthlyTs.id === timesheet?.id)
      );
    }
  }, [operatingPartners, timesheet?.id]);

  const redirectToSingleTs = () => {
    const queryParam = yearQueryParam ? `?year=${yearQueryParam}` : '';
    navigate(`/${ROUTES.ACCOUNTANT_TIMESHEETS_STATUSES}/${timesheet?.id}${queryParam}`);
  };

  const buttonStyle = useMemo<React.CSSProperties>(() => {
    if (isPrimary) return { background: colors.surfaceAccent.default, color: colors.textAccent.inverse };
    if (isTSDisabled)
      return { background: colors.surfaceInteraction.selectedLight, color: colors.icon.disabled };
    return {
      background: colors.surfaceBackground.bg1,
      color: colors.text.main,
      border: `1px solid ${colors.border.default}`,
    };
  }, [colors, isPrimary, isTSDisabled]);

  // Issued and Closed share the secondary style; a hollow ring (waiting) vs a filled check (done)
  // tells them apart at a glance.
  const statusIcon = useMemo(() => {
    if (statusLabel === TimesheetStatusLabel.ISSUED)
      return { icon: <IssuedIcon />, color: colors.icon.default };
    if (statusLabel === TimesheetStatusLabel.CLOSED)
      return { icon: <ClosedIcon />, color: colors.iconStatus.success };
    return null;
  }, [colors, statusLabel]);

  const isPending = useMemo(() => {
    return (
      statusLabel === TimesheetStatusLabel.PENDING_DEAL_OWNERS ||
      statusLabel === TimesheetStatusLabel.PENDING_OP_MANAGER ||
      statusLabel === TimesheetStatusLabel.PENDING_SVP_MANAGER
    );
  }, [statusLabel]);

  const renderPendingLabel = useCallback(
    (prefix: string, pendingLog: TimesheetLog, maxCycle: number) => {
      const pendingDuration = calculateDaysBetweenDates(pendingLog.actionStart);
      const pendingBy = pendingLog?.createdBy?.name?.trim().split(/\s+/) ?? [];

      return (
        <>
          <Typography
            variant='body'
            color={colors.text.caption}
            style={{
              width: '36px',
              textAlign: 'left',
            }}
          >
            {prefix}:
          </Typography>
          <Box
            display={'flex'}
            gap={'3px'}
            flexGrow={1}
            sx={{
              marginRight: maxCycle > 1 ? '5px' : '0px',
            }}
          >
            <Typography
              variant='body'
              color={colors.text.main}
              style={{
                maxWidth: maxCycle > 1 ? '66px' : '85px',
                minWidth: '40px',
              }}
            >
              {pendingBy?.length > 1
                ? `${pendingBy[0][0]} ${pendingBy.slice(1).join(' ')}`
                : pendingLog?.createdBy?.name}
            </Typography>
            <Typography variant='body' color={colors.text.main}>
              ({pendingDuration}d)
            </Typography>
          </Box>
          {maxCycle > 1 && (
            <Typography variant='subtitle1' color={colors.text.secondary}>
              {maxCycle}
            </Typography>
          )}
        </>
      );
    },
    [colors.text.caption, colors.text.main, colors.text.secondary]
  );

  const cellLabel = useMemo(() => {
    if (statusLabel === TimesheetStatusLabel.SUBMITTED) return 'Review';

    const sortedLogs = timesheet?.timesheetLogs?.slice().sort((a, b) => a.id - b.id);
    const maxCycle = Math.max(...(sortedLogs?.map((log) => log.cycle) || []));
    const currentCycleLogs = sortedLogs?.filter((log) => log.cycle === maxCycle);

    if (statusLabel === TimesheetStatusLabel.PENDING_DEAL_OWNERS) {
      const currentDealOwnerIds = new Set(
        (timesheet?.timesheetRows ?? [])
          .filter((row) => row.dealOwners?.length && row.avg > 0)
          .flatMap((row) => row.dealOwners?.map((owner) => owner.id) ?? [])
      );

      const pendingLog = currentCycleLogs?.find(
        (log) =>
          log.type === TimesheetLogType.PENDING &&
          log.role === UserRole.DEAL_OWNER &&
          currentDealOwnerIds.has(log.createdBy.id)
      );

      if (pendingLog) return renderPendingLabel('DO', pendingLog, maxCycle);
    }

    if (statusLabel === TimesheetStatusLabel.PENDING_OP_MANAGER) {
      const pendingLog = currentCycleLogs?.find(
        (log) => log.type === TimesheetLogType.PENDING && log.role === UserRole.OP_MANAGER
      );

      if (pendingLog) return renderPendingLabel('OM', pendingLog, maxCycle);
    }

    if (statusLabel === TimesheetStatusLabel.PENDING_SVP_MANAGER) {
      // SVP Manager approval logs carry a null role and are identified by their stage.
      const pendingLog = currentCycleLogs?.find(
        (log) => log.type === TimesheetLogType.PENDING && log.stage === WorkflowStage.SVP_MANAGER
      );

      if (pendingLog) return renderPendingLabel('SM', pendingLog, maxCycle);
    }

    return statusLabel;
  }, [statusLabel, timesheet, renderPendingLabel]);

  const progressPercentage = useMemo(() => {
    const allOpManagersCount = operatingPartner?.operatingPartnerManagers?.length ?? 0;
    const approvedManagersCount = timesheet?.approvedByOpManagers?.length ?? 0;

    const allDeals = timesheet?.timesheetRows.filter(
      (row) => row.dealOwners?.length && row.avg > 0
    );
    const allDealsCount = allDeals?.length ?? 0;
    const approvedDealsCount = allDeals?.filter((deal) => deal.isApprovedByDealOwner).length ?? 0;

    if (
      timesheet?.status === TimesheetStatus.PENDING_OP_MANAGER &&
      approvedManagersCount === 0 &&
      allOpManagersCount > 0
    ) {
      return 3;
    } else if (
      timesheet?.status === TimesheetStatus.PENDING_DEAL_OWNERS &&
      allOpManagersCount === 0 &&
      approvedDealsCount === 0 &&
      allDealsCount > 0
    ) {
      return 3;
    }

    return ((approvedDealsCount + approvedManagersCount) / (allDealsCount + allOpManagersCount)) *
      100 >
      100
      ? 100
      : ((approvedDealsCount + approvedManagersCount) / (allDealsCount + allOpManagersCount)) * 100;
  }, [
    operatingPartner?.operatingPartnerManagers,
    timesheet?.approvedByOpManagers,
    timesheet?.timesheetRows,
    timesheet?.status,
  ]);

  const progressColor = useMemo(() => {
    const sortedLogs = timesheet?.timesheetLogs?.slice().sort((a, b) => a.id - b.id);
    const maxCycle = Math.max(...(sortedLogs?.map((log) => log.cycle) || []));
    const pendingTs = sortedLogs?.find(
      (log) => log.type === TimesheetLogType.PENDING && log.cycle === maxCycle
    );
    if (!pendingTs) return colors.textAccent.default;

    if (calculateDaysBetweenDates(pendingTs.actionStart, pendingTs?.actionEnd) > 6)
      return colors.surfaceIndicator.error;
    else if (calculateDaysBetweenDates(pendingTs.actionStart, pendingTs?.actionEnd) > 3)
      return colors.surfaceIndicator.needsDetails;
    return colors.textAccent.default;
  }, [
    colors.surfaceIndicator.error,
    colors.surfaceIndicator.needsDetails,
    colors.textAccent.default,
    timesheet?.timesheetLogs,
  ]);

  if (!timesheet) return <></>;

  if (isPending)
    return (
      <PendingStatusTooltip timesheet={timesheet} operatingPartner={operatingPartner}>
        <Box display={'flex'} flexDirection={'column'} justifyContent={'start'}>
          <Button
            variant='text'
            disabled={isTSDisabled}
            style={{
              ...buttonStyle,
              width: '100%',
              height: '90%',
              borderRadius: '4px 4px 0px 0px',
            }}
            onClick={redirectToSingleTs}
          >
            {cellLabel}
          </Button>
          <ProgressBar progress={progressPercentage} color={progressColor} height='5px' />
        </Box>
      </PendingStatusTooltip>
    );

  return (
    <Box display={'flex'} flexDirection={'column'} justifyContent={'start'}>
      <Button
        variant='text'
        disabled={isTSDisabled}
        startIcon={statusIcon?.icon}
        style={{
          ...buttonStyle,
          width: '100%',
          height: '90%',
          borderRadius: '4px',
        }}
        sx={{
          '.MuiButton-startIcon': { marginRight: '6px' },
          '.MuiButton-startIcon path': { fill: statusIcon?.color },
        }}
        onClick={redirectToSingleTs}
      >
        {cellLabel}
      </Button>
    </Box>
  );
}
